import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LifeBuoy, Search, RefreshCw, ChevronRight } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const STATUS_LABELS = { open: "Ouvert", in_progress: "En cours", resolved: "Résolu", closed: "Fermé" };
const STATUS_STYLES = {
  open: "bg-red-50 text-red-700 border-red-200",
  in_progress: "bg-amber-50 text-amber-700 border-amber-200",
  resolved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  closed: "bg-stone-100 text-stone-500 border-stone-200",
};
const PRIORITY_LABELS = { low: "Faible", normal: "Normal", high: "Haute", urgent: "Urgent" };
const PRIORITY_STYLES = {
  low: "text-stone-400",
  normal: "text-stone-600",
  high: "text-amber-600",
  urgent: "text-red-600 font-semibold",
};

function fmt(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

export default function SuperSupport() {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [total, setTotal] = useState(0);
  const [openCount, setOpenCount] = useState(0);
  const [inProgressCount, setInProgressCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const load = () => {
    setLoading(true);
    const params = {};
    if (statusFilter !== "all") params.status = statusFilter;
    if (search) params.search = search;
    api.get("/superadmin/support/tickets", { params })
      .then(({ data }) => {
        setTickets(data.tickets);
        setTotal(data.total);
        setOpenCount(data.open_count);
        setInProgressCount(data.in_progress_count);
      })
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, statusFilter]);

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <LifeBuoy className="h-6 w-6 text-copper shrink-0" strokeWidth={1.5} />
          <div className="min-w-0">
            <h1 className="font-serif text-2xl text-ink">Support</h1>
            <p className="text-ash text-sm truncate">{total} ticket{total !== 1 ? "s" : ""} au total</p>
          </div>
        </div>
        <button onClick={load} className="flex items-center gap-2 text-ash hover:text-ink text-xs uppercase tracking-widest transition-colors">
          <RefreshCw className="h-4 w-4" /> Actualiser
        </button>
      </div>

      {/* KPI rapides */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {[
          { label: "Ouverts", value: openCount, color: "text-red-600" },
          { label: "En cours", value: inProgressCount, color: "text-amber-600" },
          { label: "Total", value: total, color: "text-ink" },
        ].map((k) => (
          <div key={k.label} className="bg-white border border-stone rounded-md p-4">
            <p className="text-xs uppercase tracking-widest text-ash mb-1">{k.label}</p>
            <p className={`text-2xl font-serif ${k.color}`}>{k.value}</p>
          </div>
        ))}
      </div>

      {/* Filtres */}
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ash" />
          <Input
            className="pl-9"
            placeholder="Rechercher par email, nom…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-44">
            <SelectValue placeholder="Statut" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les statuts</SelectItem>
            <SelectItem value="open">Ouvert</SelectItem>
            <SelectItem value="in_progress">En cours</SelectItem>
            <SelectItem value="resolved">Résolu</SelectItem>
            <SelectItem value="closed">Fermé</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Liste */}
      {loading ? (
        <div className="text-center py-12 text-ash text-sm">Chargement…</div>
      ) : tickets.length === 0 ? (
        <div className="text-center py-12 text-ash text-sm">Aucun ticket trouvé</div>
      ) : (
        <div className="bg-white border border-stone rounded-md overflow-hidden">
          <div className="hidden sm:grid grid-cols-[1fr_1fr_auto_auto_auto] gap-4 px-4 py-2 text-xs uppercase tracking-widest text-ash border-b border-stone">
            <span>Client</span>
            <span>Sujet</span>
            <span>Priorité</span>
            <span>Statut</span>
            <span />
          </div>
          <ul className="divide-y divide-stone">
            {tickets.map((t) => (
              <li key={t.id}>
                <button
                  onClick={() => navigate(`/admin/support/tickets/${t.id}`)}
                  className="w-full text-left px-4 py-3 hover:bg-cream/50 transition-colors"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-sm text-ink truncate">{t.user_name || t.user_email}</p>
                      <p className="text-xs text-ash truncate">{t.user_email}</p>
                      <p className="text-xs text-ash sm:hidden mt-0.5">{t.subject}</p>
                    </div>
                    <div className="hidden sm:block flex-1 min-w-0">
                      <p className="text-sm text-ink truncate">{t.subject}</p>
                      <p className="text-xs text-ash">{fmt(t.created_at)}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className={`hidden sm:inline text-xs ${PRIORITY_STYLES[t.priority] || ""}`}>
                        {PRIORITY_LABELS[t.priority] || t.priority}
                      </span>
                      <span className={`text-xs border rounded px-2 py-0.5 ${STATUS_STYLES[t.status] || "bg-stone-100 text-stone-500"}`}>
                        {STATUS_LABELS[t.status] || t.status}
                      </span>
                      <ChevronRight className="h-4 w-4 text-ash" />
                    </div>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
